import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';

import { useLetterhead } from '@/api/bookings';
import { useRentalRevenue, type RevenueGrain, type RevenueRow } from '@/api/reports';
import { DataView, type DataViewColumn } from '@/components/DataView';
import { DatePicker } from '@/components/DatePicker';
import { Button, Card, EmptyState, Notice, PageHeading } from '@/components/PageHeading';
import type { RevenueDesign } from '@core/api/reports';
import { formatAmount } from '@core/format/money';
import {
  changeOn,
  changeWords,
  dayRowsAllowed,
  grainWords,
  naturalGrain,
  perHire,
  periodRange,
  periodWords,
  revenueFigures,
  revenueKinds,
  revenueRangeProblem,
  revenueSentence,
  revenueSheet,
  rowLabel,
  shareOf,
  shortAmount,
  twelveBars,
  type PeriodChoice,
} from '@core/reports/rentalRevenue';
import type { Session } from '@core/session/types';
import { PrintFrame } from '../print/PrintFrame';
import { reportPaperFor, reportPapers } from '../print/paper';
import { RentalRevenueSheet } from '../print/RentalRevenueSheet';
import { saveWorkbook } from '../units/import/workbook';
import { RevenueInvoicesDialog } from './RevenueInvoicesDialog';

const rupees = (value: number) => `₹${formatAmount(value)}`;
const iso = (value: Dayjs) => value.format('YYYY-MM-DD');
const choices: PeriodChoice[] = ['thisMonth', 'lastMonth', 'thisYear', 'lastYear', 'custom'];
const grains: RevenueGrain[] = ['Day', 'Month', 'Year'];

/** A chip on the band: a white pill when chosen, an outline otherwise. White is written out (`#ffffff`). */
function BandChip({
  pressed,
  disabled = false,
  onClick,
  children,
}: {
  pressed: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      disabled={disabled}
      onClick={onClick}
      className={`h-9 cursor-pointer rounded-full px-3.5 text-[13px] font-semibold disabled:cursor-not-allowed disabled:opacity-40 ${
        pressed
          ? 'bg-[#ffffff] text-[var(--color-hero)]'
          : 'border border-[#ffffff]/45 text-[#ffffff] hover:bg-[#ffffff]/10'
      }`}
    >
      {children}
    </button>
  );
}

/** "▲ 23%" in the accent, "▼ 6%" in the warning ink, a dash where last year had nothing. */
function Change({ now, before }: { now: number; before: number }) {
  const change = changeOn(now, before);

  return (
    <span
      className={`font-semibold ${change === null ? 'text-default' : change.up ? 'text-primary' : 'text-warning'}`}
    >
      {changeWords(change)}
    </span>
  );
}

/**
 * Reports › Rental revenue — report C2, board D of canvas "Rental revenue" (owner, 2026-10-30): one
 * period picker on the band drives the figures against last year, the twelve-month strip, the
 * register by period, where the money came from by category and design, and — for a company-wide
 * reader — the split by branch. Every amount is the server's; the periods, words and sheet are the
 * core's (`reports/rentalRevenue.ts`). Offered only with `Report:ViewFinancials`.
 */
export function RentalRevenueReport({ session, onBack }: { session: Session; onBack: () => void }) {
  const branchId = session.branch?.id;
  const today = iso(dayjs());
  const [choice, setChoice] = useState<PeriodChoice>('thisYear');
  const [picked, setPicked] = useState(() => periodRange('thisMonth', today)!);
  const [chosenGrain, setChosenGrain] = useState<RevenueGrain | null>(null);
  const [opened, setOpened] = useState<RevenueRow | null>(null);
  const [printing, setPrinting] = useState(false);

  const range = periodRange(choice, today) ?? picked;
  // A row per day only where the server reads one; otherwise the range's own natural row.
  const grain =
    chosenGrain === 'Day' && !dayRowsAllowed(range.from, range.to)
      ? 'Month'
      : (chosenGrain ?? naturalGrain(range.from, range.to));
  const problem = revenueRangeProblem(range.from, range.to, grain);
  const report = useRentalRevenue(branchId, range.from, range.to, grain, problem === null);
  const letterhead = useLetterhead(branchId);
  const data = report.data;
  const periodName = choice === 'custom' ? null : periodWords[choice];

  if (printing && data) {
    return (
      <PrintFrame
        onClose={() => setPrinting(false)}
        title="Rental revenue"
        sizes={reportPapers}
        start={reportPaperFor(revenueKinds.length + 5)}
      >
        {(paper) => (
          <RentalRevenueSheet
            paper={paper}
            report={data}
            grain={grain}
            letterhead={letterhead.data}
            branchName={session.branch?.name ?? ''}
          />
        )}
      </PrintFrame>
    );
  }

  const pick = (end: 'from' | 'to') => (value: Dayjs | null) => {
    if (value) {
      setPicked((current) => ({ ...current, [end]: iso(value) }));
    }
  };

  const exportSheet = () => {
    if (!data) {
      return;
    }

    const sheet = revenueSheet(data, grain);

    void saveWorkbook(
      `rental-revenue-${data.from}-to-${data.to}.xlsx`,
      'Rental revenue',
      sheet.headers,
      sheet.rows,
    );
  };

  const amount = (value: number, bold = false) => (
    <span className={`font-mono-nums text-xs ${bold ? 'font-bold text-gray-900' : ''}`}>
      {value === 0 ? '—' : formatAmount(value)}
    </span>
  );

  const rowColumns: DataViewColumn<RevenueRow>[] = [
    {
      title: grainWords[grain],
      key: 'period',
      primary: true,
      render: (_, row) => (
        <button
          type="button"
          aria-haspopup="dialog"
          onClick={() => setOpened(row)}
          className="hover:text-primary inline-flex items-center gap-1.5 text-xs font-semibold text-gray-900"
        >
          <i className="icon icon-chevron-right" aria-hidden="true" />
          {rowLabel(row, grain)}
        </button>
      ),
    },
    ...revenueKinds.map((kind): DataViewColumn<RevenueRow> => ({
      title: kind.label,
      key: kind.key,
      align: 'right',
      hideOnCard: kind.key !== 'rent',
      render: (_, row) => amount(row.kinds[kind.key]),
    })),
    {
      title: 'Before GST',
      key: 'earned',
      align: 'right',
      render: (_, row) => amount(row.earned, true),
    },
    {
      title: 'GST',
      key: 'gst',
      align: 'right',
      hideOnCard: true,
      render: (_, row) => <span className="text-default">{amount(row.gst)}</span>,
    },
    { title: 'Billed', key: 'billed', align: 'right', render: (_, row) => amount(row.billed) },
    {
      title: 'On last year',
      key: 'lastYear',
      align: 'right',
      trailing: true,
      render: (_, row) => (
        <span className="text-xs">
          <Change now={row.earned} before={row.lastYearEarned} />
        </span>
      ),
    },
  ];

  const designColumns: DataViewColumn<RevenueDesign>[] = [
    {
      title: 'Design',
      key: 'design',
      primary: true,
      render: (_, design) => (
        <span className="min-w-0">
          <span className="block text-xs font-semibold text-gray-900">{design.name}</span>
          {design.itemId !== null && (
            <span className="text-default block text-[11px]">
              <span className="font-mono-nums">{design.code}</span> · {design.categoryName}
            </span>
          )}
        </span>
      ),
    },
    { title: 'Hires', key: 'hires', align: 'right', render: (_, d) => amount(d.hires) },
    {
      title: 'Per hire',
      key: 'perHire',
      align: 'right',
      hideOnCard: true,
      render: (_, d) => {
        const each = perHire(d.earned, d.hires);

        return (
          <span className="font-mono-nums text-default text-xs">
            {each === null ? '—' : rupees(each)}
          </span>
        );
      },
    },
    {
      title: 'Share',
      key: 'share',
      hideOnCard: true,
      render: (_, d) => (
        <div className="bg-primary-transparent h-2 w-28 overflow-hidden rounded-full">
          <div
            className="bg-primary h-2"
            style={{ width: `${shareOf(d.earned, data?.earned ?? 0) * 100}%` }}
          />
        </div>
      ),
    },
    {
      title: 'Earned',
      key: 'earned',
      align: 'right',
      trailing: true,
      render: (_, d) => (
        <span className="font-mono-nums text-xs font-bold text-gray-900">{rupees(d.earned)}</span>
      ),
    },
  ];

  const branches = data?.branches && data.branches.length > 1 ? data.branches : null;

  return (
    <>
      <PageHeading
        eyebrow="Reports · Money"
        title="Rental revenue"
        subtitle={
          data ? revenueSentence(data, periodName, rupees) : problem ? '' : 'Reading the invoices…'
        }
        actions={
          <>
            <Button
              icon="icon icon-printer"
              shortcut="print"
              disabled={!data}
              onClick={() => setPrinting(true)}
            >
              Print
            </Button>
            <Button icon="icon icon-file-spreadsheet" disabled={!data} onClick={exportSheet}>
              Excel
            </Button>
            <Button icon="icon icon-arrow-left" shortcut="back" onClick={onBack}>
              Reports
            </Button>
          </>
        }
      >
        <div className="flex flex-wrap items-center gap-2">
          <div role="group" aria-label="Period" className="flex flex-wrap gap-2">
            {choices.map((option) => (
              <BandChip
                key={option}
                pressed={choice === option}
                onClick={() => {
                  setChoice(option);
                  setChosenGrain(null);
                }}
              >
                {periodWords[option]}
              </BandChip>
            ))}
          </div>
          {choice === 'custom' && (
            <div className="flex flex-wrap items-center gap-2">
              <DatePicker
                aria-label="From"
                value={dayjs(picked.from)}
                allowClear={false}
                disabledDate={(value) => value.isAfter(dayjs(), 'day')}
                onChange={pick('from')}
              />
              <span className="text-[#ffffff]">to</span>
              <DatePicker
                aria-label="To"
                value={dayjs(picked.to)}
                allowClear={false}
                disabledDate={(value) => value.isAfter(dayjs(), 'day')}
                onChange={pick('to')}
              />
            </div>
          )}
          <div
            role="group"
            aria-label="A row per"
            className="ms-auto flex items-center gap-2 text-[12px] text-(--color-hero-muted)"
          >
            A row per
            {grains.map((option) => (
              <BandChip
                key={option}
                pressed={grain === option}
                disabled={option === 'Day' && !dayRowsAllowed(range.from, range.to)}
                onClick={() => setChosenGrain(option)}
              >
                {grainWords[option]}
              </BandChip>
            ))}
          </div>
        </div>
      </PageHeading>

      {problem && (
        <div className="mb-4">
          <Notice tone="warning" title={problem}>
            Choose a shorter range, or a row per month.
          </Notice>
        </div>
      )}

      {data && (
        <div className="flex flex-col gap-4">
          <Card>
            <ul className="m-0 grid grid-cols-2 gap-4 ps-0 md:grid-cols-3 xl:grid-cols-6">
              {revenueFigures(data).map((figure) => (
                <li key={figure.key} className="mb-0 flex list-none flex-col gap-0.5">
                  <span className="text-default text-[12px]">{figure.label}</span>
                  <span className="font-serif text-[24px] leading-tight font-semibold text-gray-900 lining-nums tabular-nums">
                    {rupees(figure.value)}
                  </span>
                  <span className="text-[12px]">
                    <span
                      className={`font-semibold ${figure.change === null ? 'text-default' : figure.change.up ? 'text-primary' : 'text-warning'}`}
                    >
                      {changeWords(figure.change)}
                    </span>{' '}
                    <span className="text-default">on last year</span>
                  </span>
                </li>
              ))}
            </ul>
          </Card>

          <Card>
            <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
              <p className="mb-0 text-[length:var(--scale-list-name)] font-semibold text-gray-900">
                The last twelve months
              </p>
              <span className="text-default text-[11px]">
                Earned before GST · the period you picked is darker
              </span>
            </div>
            <div
              role="img"
              aria-label={twelveBars(data)
                .map((bar) => `${dayjs(bar.month).format('MMM YYYY')} ${rupees(bar.earned)}`)
                .join(', ')}
              className="grid h-40 grid-cols-12 items-end gap-2"
            >
              {twelveBars(data).map((bar) => (
                <div
                  key={bar.month}
                  className="flex h-full flex-col items-center justify-end gap-1"
                >
                  <span className="text-default text-[10px]">{shortAmount(bar.earned)}</span>
                  <div
                    className={`w-full max-w-11 rounded-t-md ${bar.inPeriod ? 'bg-primary' : 'bg-primary-transparent'}`}
                    style={{
                      height: `${Math.max(bar.height * 100, bar.earned > 0 ? 2 : 0) * 0.85}%`,
                    }}
                  />
                  <span className="text-[11px] font-semibold text-gray-900">{bar.short}</span>
                </div>
              ))}
            </div>
          </Card>

          <Card flush>
            <div className="flex flex-wrap items-center justify-between gap-2 px-5 pt-4 pb-2">
              <p className="mb-0 text-[length:var(--scale-list-name)] font-semibold text-gray-900">
                {grain === 'Day'
                  ? 'Day by day'
                  : grain === 'Month'
                    ? 'Month by month'
                    : 'Year by year'}
              </p>
              <span className="text-default text-[11px]">Open a row to see its invoices</span>
            </div>
            <DataView
              columns={rowColumns}
              dataSource={data.rows}
              rowKey="from"
              loading={report.isFetching}
              empty={<EmptyState icon="icon icon-receipt" title="No invoices in this period" />}
            />
            <div className="border-border-color flex flex-wrap justify-end gap-x-6 gap-y-1 border-t px-5 py-3 text-xs">
              <span className="text-default">
                Before GST{' '}
                <span className="font-mono-nums font-bold text-gray-900">
                  {rupees(data.earned)}
                </span>
              </span>
              <span className="text-default">
                GST{' '}
                <span className="font-mono-nums font-bold text-gray-900">{rupees(data.gst)}</span>
              </span>
              <span className="text-default">
                Billed{' '}
                <span className="font-mono-nums font-bold text-gray-900">
                  {rupees(data.billed)}
                </span>
              </span>
              <span className="text-default">
                On last year <Change now={data.earned} before={data.lastYearEarned} />
              </span>
            </div>
          </Card>

          <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)]">
            <Card>
              <p className="mb-3 text-[length:var(--scale-list-name)] font-semibold text-gray-900">
                By category
              </p>
              {data.categories.length === 0 ? (
                <p className="text-default mb-0 text-xs">Nothing earned in this period.</p>
              ) : (
                <ul className="m-0 flex flex-col gap-3 ps-0">
                  {data.categories.map((category) => (
                    <li key={category.categoryId ?? 'untied'} className="mb-0 list-none">
                      <span className="flex justify-between gap-2 text-xs">
                        <span className="font-semibold text-gray-900">{category.name}</span>
                        <span className="font-mono-nums font-bold text-gray-900">
                          {rupees(category.earned)}
                        </span>
                      </span>
                      <div className="bg-primary-transparent mt-1.5 h-2.5 overflow-hidden rounded-full">
                        <div
                          className="bg-primary h-2.5"
                          style={{
                            width: `${shareOf(category.earned, data.categories[0]?.earned ?? 0) * 100}%`,
                          }}
                        />
                      </div>
                      <span className="text-default text-[11px]">
                        {Math.round(shareOf(category.earned, data.earned) * 100)}% ·{' '}
                        {category.hires} {category.hires === 1 ? 'hire' : 'hires'}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card flush>
              <div className="flex flex-wrap items-center justify-between gap-2 px-5 pt-4 pb-2">
                <p className="mb-0 text-[length:var(--scale-list-name)] font-semibold text-gray-900">
                  By design
                </p>
                <span className="text-default text-[11px]">
                  Highest earner first · a hire is one garment handed over
                </span>
              </div>
              <DataView
                columns={designColumns}
                dataSource={data.designs}
                rowKey="itemId"
                empty={
                  <EmptyState icon="icon icon-shirt" title="No designs earned in this period" />
                }
              />
            </Card>
          </div>

          {branches && (
            <Card>
              <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
                <p className="mb-0 text-[length:var(--scale-list-name)] font-semibold text-gray-900">
                  By branch
                </p>
                <span className="text-default text-[11px]">
                  The company’s branches — everything above is{' '}
                  {session.branch?.name ?? 'this branch'}
                </span>
              </div>
              <ul className="m-0 grid grid-cols-1 gap-4 ps-0 md:grid-cols-2">
                {branches.map((branch) => (
                  <li key={branch.branchId} className="mb-0 list-none">
                    <span className="flex justify-between gap-2 text-xs">
                      <span className="font-semibold text-gray-900">{branch.name}</span>
                      <span className="font-mono-nums font-bold text-gray-900">
                        {rupees(branch.earned)}
                      </span>
                    </span>
                    <div className="bg-primary-transparent mt-1.5 h-2.5 overflow-hidden rounded-full">
                      <div
                        className="bg-primary h-2.5"
                        style={{
                          width: `${
                            shareOf(
                              branch.earned,
                              branches.reduce((sum, b) => sum + b.earned, 0),
                            ) * 100
                          }%`,
                        }}
                      />
                    </div>
                    <span className="text-default text-[11px]">
                      {Math.round(
                        shareOf(
                          branch.earned,
                          branches.reduce((sum, b) => sum + b.earned, 0),
                        ) * 100,
                      )}
                      % · {branch.hires} {branch.hires === 1 ? 'hire' : 'hires'}
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      )}

      {!data && !problem && report.isError && (
        <Card>
          <EmptyState icon="icon icon-receipt" title="The report could not be read" />
        </Card>
      )}

      <p className="text-default mt-3 text-[11px]">
        From the rental invoices, by invoice date; cancelled invoices are left out. Earned is before
        GST, and rent includes extensions. Deposits are not revenue. A hire is one garment handed
        over in the period.
      </p>

      {opened && (
        <RevenueInvoicesDialog
          branchId={branchId}
          row={opened}
          label={rowLabel(opened, grain)}
          onClose={() => setOpened(null)}
        />
      )}
    </>
  );
}
